import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface SystemHealth {
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
  lastCheck: string;
}

interface MoolahContextType {
  systemHealth: SystemHealth;
  isLoading: boolean;
  refreshHealth: () => Promise<void>;
}

const MoolahContext = createContext<MoolahContextType | undefined>(undefined);

interface MoolahProviderProps {
  children: ReactNode;
}

export const MoolahProvider: React.FC<MoolahProviderProps> = ({ children }) => {
  const [systemHealth, setSystemHealth] = useState<SystemHealth>({
    status: 'unknown',
    lastCheck: new Date().toISOString(),
  });
  const [isLoading, setIsLoading] = useState(true);

  const refreshHealth = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch('/api/v1/admin/health');
      if (res.ok) {
        const data = await res.json();
        setSystemHealth({
          status: data.success ? 'healthy' : 'warning',
          lastCheck: new Date().toISOString(),
        });
      } else {
        setSystemHealth({ status: 'warning', lastCheck: new Date().toISOString() });
      }
    } catch {
      setSystemHealth({ status: 'critical', lastCheck: new Date().toISOString() });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshHealth();

    // Poll every 2 min, but only while the tab is visible. Background tabs must not burn
    // the server-side rate-limit budget — previously every open tab polled /health every
    // 60s, and (before the per-endpoint bucket fix) that pooled against the shared admin
    // read bucket and triggered 429s. Pausing when hidden also avoids needless DB COUNTs.
    let interval: ReturnType<typeof setInterval> | null = setInterval(refreshHealth, 120_000);
    const clear = () => { if (interval) { clearInterval(interval); interval = null; } };

    const onVisibility = () => {
      if (document.hidden) {
        clear();
      } else {
        refreshHealth();
        interval = setInterval(refreshHealth, 120_000);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshHealth]);

  return (
    <MoolahContext.Provider value={{ systemHealth, isLoading, refreshHealth }}>
      {children}
    </MoolahContext.Provider>
  );
};

export const useMoolah = (): MoolahContextType => {
  const context = useContext(MoolahContext);
  if (context === undefined) {
    throw new Error('useMoolah must be used within a MoolahProvider');
  }
  return context;
};
